import React from "react";

/**
 * Minimal SVG sparkline.
 * Renders a 280x60 area+line chart of `values` (null entries = gaps).
 * The dot on the last numeric value is highlighted.
 */
export default function Sparkline({ values, color = "#1F3864", height = 60, width = 280, formatValue }) {
  const numeric = values.filter((v) => v != null && !Number.isNaN(v));
  if (numeric.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-[10px] text-[#94A3B8] italic"
        style={{ height, width }}
      >
        dados insuficientes
      </div>
    );
  }
  const max = Math.max(...numeric);
  const min = Math.min(...numeric);
  const range = max - min || 1;
  const n = values.length;
  const stepX = width / (n - 1);

  const points = values.map((v, i) => {
    if (v == null) return null;
    const x = i * stepX;
    const y = height - 6 - ((v - min) / range) * (height - 12);
    return { x, y, v, i };
  });

  // Build path segments (skip nulls)
  const segments = [];
  let current = [];
  for (const p of points) {
    if (p == null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push(p);
    }
  }
  if (current.length) segments.push(current);

  const last = [...points].reverse().find((p) => p != null);
  const first = points.find((p) => p != null);
  const lastVal = last?.v ?? null;
  const firstVal = first?.v ?? null;
  const delta = lastVal != null && firstVal != null ? lastVal - firstVal : null;

  return (
    <div className="relative">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="sparkline"
      >
        {segments.map((seg, si) => {
          if (seg.length < 2) {
            const p = seg[0];
            return <circle key={si} cx={p.x} cy={p.y} r={2} fill={color} />;
          }
          const d = seg.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
          const areaPath = `${d} L ${seg[seg.length - 1].x.toFixed(1)} ${height} L ${seg[0].x.toFixed(1)} ${height} Z`;
          return (
            <g key={si}>
              <path d={areaPath} fill={color} opacity="0.08" />
              <path d={d} fill="none" stroke={color} strokeWidth="1.5" />
            </g>
          );
        })}
        {last && (
          <circle cx={last.x} cy={last.y} r={3} fill="#fff" stroke={color} strokeWidth="1.5" />
        )}
      </svg>
      {lastVal != null && (
        <div className="absolute top-1 right-1 text-[10px] font-mono font-semibold text-[#1A1A2E] bg-white/80 px-1 rounded-sm">
          {formatValue ? formatValue(lastVal) : lastVal}
          {delta != null && delta !== 0 && (
            <span className={`ml-1 ${delta > 0 ? "text-[#28A745]" : "text-[#DC3545]"}`}>
              {delta > 0 ? "▲" : "▼"}
              {Math.abs(delta) < 10 ? Math.abs(delta).toFixed(1) : Math.round(Math.abs(delta))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
